
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, Award } from 'lucide-react';
import ImageUpload from './ImageUpload';

interface AchievementItem {
    id: string;
    title: string;
    description: string;
    date: string;
    category: 'certification' | 'award' | 'recognition';
    issuer: string;
    image?: string;
    imageAlt?: string;
}

export default function AchievementsManager() {
    const [items, setItems] = useState<AchievementItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<AchievementItem>>({
        title: '',
        description: '',
        date: '',
        category: 'certification',
        issuer: '',
        image: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'about_achievements')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setItems(data.content);
            }
        } catch (err) {
            console.error('Error fetching achievements:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: AchievementItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'about_achievements',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setItems(updatedList);
            setIsFormOpen(false);
            setNewItem({ title: '', description: '', date: '', category: 'certification', issuer: '', image: '' });
        } catch (err) {
            console.error('Error saving achievements:', err);
            alert('Failed to save achievements');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.title || !newItem.issuer) return;

        const itemToAdd: AchievementItem = {
            id: Math.random().toString(36).substr(2, 9),
            title: newItem.title!,
            description: newItem.description || '',
            date: newItem.date || '',
            category: newItem.category || 'certification',
            issuer: newItem.issuer!,
            image: newItem.image,
            imageAlt: `${newItem.title} badge`
        };

        const updated = [...items, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this achievement?')) return;
        const updated = items.filter(i => i.id !== id);
        handleSave(updated);
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'certification': return 'bg-blue-900/30 text-blue-400 border-blue-900';
            case 'award': return 'bg-yellow-900/30 text-yellow-400 border-yellow-900';
            case 'recognition': return 'bg-purple-900/30 text-purple-400 border-purple-900';
            default: return 'bg-gray-900/30 text-gray-400 border-gray-900';
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">Achievements</h2>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    {isFormOpen ? 'Cancel' : <><Plus size={18} /> Add Achievement</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                            <input
                                type="text"
                                value={newItem.title}
                                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., AWS Solutions Architect Professional"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Issuer</label>
                            <input
                                type="text"
                                value={newItem.issuer}
                                onChange={(e) => setNewItem({ ...newItem, issuer: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., Amazon Web Services"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Date</label>
                            <input
                                type="text"
                                value={newItem.date}
                                onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., March 2023"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                            <select
                                value={newItem.category}
                                onChange={(e) => setNewItem({ ...newItem, category: e.target.value as any })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            >
                                <option value="certification">Certification</option>
                                <option value="award">Award</option>
                                <option value="recognition">Recognition</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                            <textarea
                                rows={3}
                                value={newItem.description}
                                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Badge/Certificate Image</label>
                        <div className="max-w-xs">
                            <ImageUpload
                                value={newItem.image || null}
                                onChange={(url) => setNewItem({ ...newItem, image: url })}
                                folder="achievements"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={addItem}
                            disabled={saving || !newItem.title || !newItem.issuer}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : 'Save Achievement'}
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col gap-4 group hover:border-gray-700 transition-colors relative">
                        <button
                            onClick={() => deleteItem(item.id)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                        >
                            <Trash2 size={18} />
                        </button>

                        {item.image && (
                            <div className="flex justify-center">
                                <img src={item.image} alt={item.title} className="h-24 w-24 object-contain rounded-lg" />
                            </div>
                        )}

                        <div>
                            <div className={`inline-block px-2 py-1 rounded text-xs font-medium mb-2 border ${getCategoryColor(item.category)}`}>
                                {item.category.toUpperCase()}
                            </div>
                            <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                            <p className="text-sm text-blue-400 font-medium">{item.issuer}</p>
                            {item.date && <p className="text-xs text-gray-500 mt-1">{item.date}</p>}
                            {item.description && <p className="text-sm text-gray-300 mt-3">{item.description}</p>}
                        </div>
                    </div>
                ))}

                {items.length === 0 && !loading && (
                    <div className="col-span-full text-center py-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No achievements added yet.
                    </div>
                )}
            </div>
        </div>
    );
}
